import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Shield, Clock, CheckCircle2, AlertCircle, FileText, ArrowLeft } from 'lucide-react';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { SplitPreview } from '../components/ui/SplitPreview';
import { getAgreementByCode, getAgreementOutcomes, registerOutcome, updateAgreement } from '../lib/persistence';
import { Agreement, AgreementOutcome, CATEGORY_CONFIG, OUTCOME_LABELS } from '../types';
export function AgreementDetail() {
  const {
    code
  } = useParams<{
    code: string;
  }>();
  const [agreement, setAgreement] = useState<Agreement | null>(null);
  const [outcomes, setOutcomes] = useState<AgreementOutcome[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  useEffect(() => {
    if (code) {
      loadData();
    }
  }, [code]);
  const loadData = async () => {
    if (!code) return;
    const data = await getAgreementByCode(code);
    setAgreement(data);
    if (data) {
      const out = await getAgreementOutcomes(data.id);
      setOutcomes(out);
    }
    setLoading(false);
  };
  if (loading) {
    return <Layout data-id="element-130">
        <div className="flex items-center justify-center min-h-[60vh]" data-id="element-131">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" data-id="element-132" />
        </div>
      </Layout>;
  }
  if (!agreement) {
    return <Layout data-id="element-133">
        <div className="max-w-md mx-auto px-4 py-16 text-center" data-id="element-134">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" data-id="element-135" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-136">
            Acuerdo no encontrado
          </h1>
        </div>
      </Layout>;
  }
  const categoryConfig = CATEGORY_CONFIG[agreement.category];
  const outcomeConfig = OUTCOME_LABELS[agreement.outcome_type];
  const handleRegisterOutcome = async () => {
    setActionLoading(true);
    try {
      await registerOutcome(agreement.id, {
        type: agreement.outcome_type,
        title: outcomeConfig.label,
        description: 'El prestador ha marcado este acuerdo como completado/entregado.'
      });
      await updateAgreement(agreement.agreement_code, {
        status: 'outcome_registered',
        outcome_at: new Date().toISOString()
      });
      await loadData();
    } catch (error) {
      console.error(error);
    } finally {
      setActionLoading(false);
    }
  };
  const handleClientConfirm = async () => {
    setActionLoading(true);
    try {
      await updateAgreement(agreement.agreement_code, {
        status: 'completed',
        confirmed_at: new Date().toISOString(),
        completed_at: new Date().toISOString(),
        escrow_state: 'RELEASED'
      });
      await loadData();
    } catch (error) {
      console.error(error);
    } finally {
      setActionLoading(false);
    }
  };
  const getStatusBadge = () => {
    switch (agreement.status) {
      case 'pending_payment':
        return <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-137">
            <Clock className="w-3.5 h-3.5" data-id="element-138" /> Esperando pago
          </span>;
      case 'active':
        return <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-139">
            <Shield className="w-3.5 h-3.5" data-id="element-140" /> Fondos protegidos
          </span>;
      case 'outcome_registered':
        return <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800" data-id="element-141">
            <FileText className="w-3.5 h-3.5" data-id="element-142" /> Esperando confirmación
          </span>;
      case 'completed':
        return <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-143">
            <CheckCircle2 className="w-3.5 h-3.5" data-id="element-144" /> Completado
          </span>;
      default:
        return <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-145">
            {agreement.status}
          </span>;
    }
  };
  return <Layout data-id="element-146">
      <div className="max-w-4xl mx-auto px-4 py-8" data-id="element-147">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 mb-6 transition-colors" data-id="element-148">
          <ArrowLeft className="w-4 h-4" data-id="element-149" /> Volver al dashboard
        </Link>

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8" data-id="element-150">
          <div data-id="element-151">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2" data-id="element-152">
              {agreement.title}
            </h1>
            <div className="flex items-center gap-3 text-sm text-gray-500" data-id="element-153">
              <span className="font-mono" data-id="element-154">{agreement.agreement_code}</span>
              <span data-id="element-155">•</span>
              <span data-id="element-156">{categoryConfig?.label}</span>
            </div>
          </div>
          <div data-id="element-157">{getStatusBadge()}</div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-158">
          <div className="md:col-span-2 space-y-6" data-id="element-159">
            {/* Acciones principales según estado */}
            <Card className="border-blue-100 bg-blue-50/30" data-id="element-160">
              <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-161">
                Estado actual
              </h3>

              {agreement.status === 'pending_payment' && <div data-id="element-162">
                  <p className="text-sm text-gray-600 mb-4" data-id="element-163">
                    Estamos esperando que el cliente ({agreement.client_email})
                    realice el pago para asegurar los fondos.
                  </p>
                  <div className="bg-white p-3 rounded-xl border border-gray-200 flex items-center justify-between" data-id="element-164">
                    <span className="text-sm font-mono text-gray-600 truncate mr-4" data-id="element-165">
                      {window.location.origin}/agreement/
                      {agreement.agreement_code}
                    </span>
                    <Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(`${window.location.origin}/agreement/${agreement.agreement_code}`)} data-id="element-166">
                      Copiar link
                    </Button>
                  </div>
                </div>}

              {agreement.status === 'active' && <div data-id="element-167">
                  <p className="text-sm text-gray-600 mb-4" data-id="element-168">
                    Los fondos están asegurados. Una vez que prestes el
                    servicio, registrá el resultado para que el cliente lo
                    confirme y se liberen los fondos.
                  </p>
                  <Button onClick={handleRegisterOutcome} loading={actionLoading} icon={<CheckCircle2 className="w-4 h-4" data-id="element-170" />} data-id="element-169">
                    {outcomeConfig?.providerCTA || 'Registrar resultado'}
                  </Button>
                </div>}

              {agreement.status === 'outcome_registered' && <div data-id="element-171">
                  <p className="text-sm text-gray-600 mb-4" data-id="element-172">
                    Registraste el resultado. Ahora el cliente debe confirmar
                    que está conforme.
                    <br data-id="element-173" />
                    <br data-id="element-174" />
                    <em data-id="element-175">(Simulación: Como cliente, podés confirmar abajo)</em>
                  </p>
                  <Button onClick={handleClientConfirm} loading={actionLoading} variant="secondary" icon={<CheckCircle2 className="w-4 h-4" data-id="element-177" />} data-id="element-176">
                    [Simular Cliente]{' '}
                    {outcomeConfig?.clientCTA || 'Confirmar conformidad'}
                  </Button>
                </div>}

              {agreement.status === 'completed' && <div className="flex items-center gap-3 text-green-700 bg-green-50 p-4 rounded-xl" data-id="element-178">
                  <CheckCircle2 className="w-6 h-6 flex-shrink-0" data-id="element-179" />
                  <div data-id="element-180">
                    <p className="font-bold" data-id="element-181">Acuerdo completado</p>
                    <p className="text-sm" data-id="element-182">
                      Los fondos han sido liberados a tu cuenta.
                    </p>
                  </div>
                </div>}
            </Card>

            {/* Detalles del acuerdo */}
            <Card data-id="element-183">
              <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-184">
                Detalles del servicio
              </h3>
              <div className="space-y-4" data-id="element-185">
                {agreement.description && <div data-id="element-186">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-187">
                      Descripción
                    </p>
                    <p className="text-sm text-gray-900 whitespace-pre-wrap" data-id="element-188">
                      {agreement.description}
                    </p>
                  </div>}
                <div className="grid grid-cols-2 gap-4" data-id="element-189">
                  <div data-id="element-190">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-191">
                      Cliente
                    </p>
                    <p className="text-sm text-gray-900" data-id="element-192">
                      {agreement.client_name || agreement.client_email}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-193">
                      {agreement.client_email}
                    </p>
                  </div>
                  <div data-id="element-194">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-195">
                      Fecha
                    </p>
                    <p className="text-sm text-gray-900" data-id="element-196">
                      {agreement.service_date ? new Date(agreement.service_date).toLocaleDateString('es-AR') : 'No especificada'}
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Historial de resultados */}
            {outcomes.length > 0 && <Card data-id="element-197">
                <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-198">
                  Historial
                </h3>
                <div className="space-y-4" data-id="element-199">
                  {outcomes.map(outcome => <div key={outcome.id} className="flex gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100" data-id="element-200">
                      <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center flex-shrink-0 shadow-sm" data-id="element-201">
                        <FileText className="w-4 h-4 text-gray-500" data-id="element-202" />
                      </div>
                      <div data-id="element-203">
                        <p className="text-sm font-bold text-gray-900" data-id="element-204">
                          {outcome.title}
                        </p>
                        <p className="text-xs text-gray-500 mb-2" data-id="element-205">
                          {new Date(outcome.created_at).toLocaleString('es-AR')}
                        </p>
                        {outcome.description && <p className="text-sm text-gray-600" data-id="element-206">
                            {outcome.description}
                          </p>}
                      </div>
                    </div>)}
                </div>
              </Card>}
          </div>

          <div className="md:col-span-1" data-id="element-207">
            <Card data-id="element-208">
              <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-209">
                Económico
              </h3>
              <SplitPreview amount={agreement.amount} currency={agreement.currency} data-id="element-210" />
            </Card>
          </div>
        </div>
      </div>
    </Layout>;
}